import { type CardViewAttributes } from './generated/CardViewAttributes';
import { randomId } from 'javascripts/random_id';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import CardGroup from './CardGroup';
import CardRow from './CardRow';
import ItemPlacement from './ItemPlacement';
import { HasCardLayoutHierarchy } from './concerns/HasCardLayoutHierarchy';
import { JsonQuerySerialization } from './concerns/JsonQuerySerialization';

interface CardView extends CardViewAttributes, HasCardLayoutHierarchy {}

@model
@HasCardLayoutHierarchy
@JsonQuerySerialization
class CardView extends ApplicationModel {
  // Start auto-generated
  static typename: 'CardView' = 'CardView';
  get typename(): 'CardView' {
    return 'CardView';
  }

  static inputType = 'CardViewAttributes';

  static fields = {
    coverImages: attr({ writable: true, readable: true }),
    inherited: attr({ writable: true, readable: true }),
    showParentRecord: attr({ writable: true, readable: true }),
    cardStyling: attr.belongsTo({
      writeShape: ['type'],
      readable: true,
      typename: 'CardStyling',
    }),
    rows: attr.hasMany({ writable: true, readable: true }),
  };
  // End auto-generated

  /**
   * Place a row in the card view
   *
   * @param row The row to place
   * @param placement The placement in the view for adding the row.
   */
  placeRow(row, placement = ItemPlacement.bottom()) {
    // @ts-ignore difference between ItemInterface and CardRow
    this.rows = placement.placeItems(this.rows, [row]);

    this.inherited = false;
  }

  /**
   * Creates a new row and adds it to this view.
   *
   * @param placement The placement in the view for adding the row.
   * @param divider Whether the row is a divider or not.
   */
  createRow(
    placement: ItemPlacement = ItemPlacement.bottom(),
    divider = false
  ) {
    const row = new CardRow({ id: randomId(), divider }, { parent: this });
    row.firstGroup = new CardGroup(
      { id: randomId(), fields: [] },
      { parent: row }
    );
    row.secondGroup = new CardGroup(
      { id: randomId(), fields: [] },
      { parent: row }
    );

    this.placeRow(row, placement);

    return row;
  }

  // Mark the view as "dirty" (not inherited) whenever a view attribute changes.
  setAttribute(...args: Parameters<ApplicationModel['setAttribute']>) {
    super.setAttribute(...args);

    const [name] = args;
    if (['coverImages', 'showParentRecord', 'cardStyling'].includes(name)) {
      this.inherited = false;
    }
  }

  removeEmptyRows() {
    this.rows = this.rows.filter(row => !row.empty);
  }

  removeRow(row: CardRow) {
    const index = this.rows.indexOf(row);
    if (index !== -1) {
      this.rows.splice(index, 1);
    }
  }

  get fields() {
    return this.rows.flatMap(row => row.fields);
  }

  parentForRecord(record) {
    if (!this.recordLayout.parentRecordIsAvailable) return;
    if (!this.showParentRecord) return;

    const { container } = record;
    if (container) {
      return {
        url: container.path,
        label: `${container.referenceNum} ${container.name}`,
      };
    }
  }

  /**
   * Compares another CardView to this, and returns whether they are equal or not.
   *
   * @param {CardView} otherView The CardView to compare.
   */
  isEqualTo(otherView: CardView): boolean {
    return [
      () => this.coverImages === otherView.coverImages,
      () => this.showParentRecord === otherView.showParentRecord,
      () => this.rows.length === otherView.rows.length,
      () => {
        if (this.cardStyling && otherView.cardStyling) {
          return this.cardStyling.type === otherView.cardStyling.type;
        }
        return this.cardStyling === otherView.cardStyling;
      },
      () =>
        this.rows.every((row, index) => row.isEqualTo(otherView.rows[index])),
    ].every(fn => fn());
  }

  /**
   * Appends the necessary scopes to the query for this layout.
   *
   * @param {Query} scope The current query to append to.
   * @returns {Query} The final query for the given layout.
   */
  buildScope(scope) {
    if (this.showParentRecord) {
      switch (this.modelClass?.typename) {
        case 'Feature':
          scope = scope.deepMerge({ epic: ['name', 'referenceNum', 'path'] });
          break;

        case 'Requirement':
          scope = scope.deepMerge({
            feature: ['name', 'referenceNum', 'path'],
          });
          break;
      }
    }

    if (this.cardStyling) {
      scope = this.cardStyling.buildScope(scope);
    }

    return this.fields.reduce((query, field) => field.buildScope(query), scope);
  }

  /**
   * Find a CardGroup in the layout by its groupId.
   *
   * @param {string} groupId The group ID.
   * @returns {CardGroup} The group.
   */
  getGroup(groupId) {
    let cardGroup;
    this.rows.find(row => (cardGroup = row.getGroup(groupId)));

    return cardGroup;
  }

  /**
   * Find the CardGroup that contains the specified field.
   *
   * @param {string} cardFieldId The ID of the CardField
   * @returns {CardGroup} The CardGroup that contains the field.
   */
  getGroupForField(cardFieldId) {
    let cardGroup;
    this.rows.find(row => (cardGroup = row.getGroupForField(cardFieldId)));

    return cardGroup;
  }

  /**
   * Removes a CardField from the layout.
   *
   * @param {CardField} cardField The CardField to remove.
   */
  removeCardField(cardField) {
    const group = this.getGroupForField(cardField.id);
    if (!group) return;

    group.removeField(cardField.id);
  }
}

export default CardView;
